import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AttendanceStatus,
  Employee,
  LeaveBalanceRow,
  LeaveRequestStatus,
  LeaveType,
} from "@somasi/shared";
import {
  ADMIN_ROLES,
  PAYROLL_APPROVE_ROLES,
  REMOTE_WORKING_STATUSES,
  halvesToDays,
  leaveTypeColour,
  withLeaveColours,
} from "@somasi/shared";
import {
  api,
  type AttendanceGridDay,
  type LeaveRequestDraft,
  type LeaveRequestRow,
  type WfhRequestRow,
} from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { AttendancePersonSelect } from "@/components/entity-select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ListSearch,
  ListToolbar,
  TablePagination,
  usePagination,
  type ListPagination,
} from "@/components/list-controls";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  History,
  Inbox,
  Laptop,
  Plus,
  User,
  Wallet,
  X,
} from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { NOTIFICATIONS_KEY } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/leave")({
  /**
   * Which tab is open, in the URL.
   *
   * So a notification can land somebody on the queue rather than on whatever
   * tab the page happens to open with — and so the tab survives a reload and
   * can be linked to. Unrecognised values are dropped rather than refused: a
   * stale link should open the page, not an error.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" && search.tab ? { tab: search.tab } : {},
  head: () => ({
    meta: [
      { title: "Leave — Somasi Ops" },
      { name: "description", content: "Request time off, and approve what the team asks for." },
    ],
  }),
  component: LeavePage,
});

/**
 * Leave requests and balances, as two screens under one route.
 *
 * The two readers of this page want opposite things. Somebody asking for time
 * off wants their own history — what they asked for, and what came of it, in
 * every state a request can end up in. Somebody deciding wants a queue, and
 * enough of the asker's year in front of them to decide without opening a second
 * screen: what they have left, what they have already booked, and where the
 * requested dates fall among it.
 *
 * Drawing both from one table of everything made each of them worse, so the
 * route branches on the role and the two views are written separately. What they
 * genuinely share — the status vocabulary, the balance tiles, the request form,
 * the year calendar — is shared as components rather than as a shape both views
 * bend to.
 *
 * Approving is also where the 2026 worksheet's `-2` is prevented: a request past
 * the balance is refused, and granting it anyway requires ticking that the excess
 * is unpaid. Either way somebody decides, on a date, rather than a deduction
 * being discovered in a payslip three weeks later.
 */

function LeavePage() {
  const { hasAny } = useAuth();
  return hasAny(PAYROLL_APPROVE_ROLES) ? <ApproverLeave /> : <MyLeave />;
}

/** The tabs this screen has, and the one a link with no `?tab=` lands on. */
const LEAVE_TABS = ["queue", "all", "calendar", "mine"] as const;
const DEFAULT_LEAVE_TAB = "queue";

/**
 * The open tab, held in the URL rather than in state.
 *
 * Controlled by the search parameter both ways: a notification linking to
 * `?tab=queue` switches the tab even when the page is already open, which it
 * would not if the parameter were only an initial value. Switching by hand
 * replaces the history entry rather than pushing one — Back should leave the
 * page, not walk through the tabs that were looked at on the way.
 */
function useLeaveTab() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const value = LEAVE_TABS.includes(tab as (typeof LEAVE_TABS)[number])
    ? (tab as string)
    : DEFAULT_LEAVE_TAB;

  return {
    value,
    onChange: (next: string) => {
      void navigate({ search: { tab: next }, replace: true });
    },
  };
}

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  approved: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  rejected: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200",
  cancelled: "bg-muted text-muted-foreground",
  draft: "bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Waiting",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  draft: "Draft",
};

/**
 * The states a request is actually found in, in the order a history reads.
 *
 * `draft` is left out of the filter deliberately: nothing in the API ever
 * produces one, so offering it would be a tab that is always empty.
 */
const FILTER_STATUSES: LeaveRequestStatus[] = ["pending", "approved", "rejected", "cancelled"];

/**
 * The value the type picker carries for work from home.
 *
 * Not a leave type id, because work from home is not a leave type: it consumes
 * no allowance, it is never an absence, and it is paid as the ordinary day it
 * is. It is offered in the same picker because to the person asking it is the
 * same act — these dates, this reason, waiting on the same manager — and making
 * them find a second screen for it is how it ends up being asked for in chat.
 *
 * A sentinel rather than a real row keeps the two apart where it matters: the
 * request goes to `/wfh-requests`, and approving it writes nothing onto the
 * attendance grid. See `docs/work-from-home-approval.md` §2 and §3.
 */
const WFH_OPTION = "__wfh__";

/**
 * The arrangements a work-from-home request can be for.
 *
 * `wfh` covers almost all of them; the other three exist because a week at a
 * client, on the road or in a classroom is the same permission — a day worked
 * away from the office — and it should read correctly on the grid afterwards
 * rather than as a day at a desk nobody saw.
 */
const REMOTE_STATUS_LABEL: Record<string, string> = {
  wfh: "Working from home",
  client_site: "At a client's site",
  travel: "Travelling",
  training: "Training",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));

/** Weeks run Sunday to Saturday, as the worksheet's own columns do. */
const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

function thisYear(): number {
  return new Date().getUTCFullYear();
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A stored date as a person reads it, without `Date` moving it a zone. */
function dayLabel(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${Number(day)} ${MONTH_SHORT[Number(month) - 1]} ${year}`;
}

function spanLabel(request: Pick<LeaveRequestRow, "start_date" | "end_date">): string {
  return request.start_date === request.end_date
    ? dayLabel(request.start_date)
    : `${dayLabel(request.start_date)} → ${dayLabel(request.end_date)}`;
}

/**
 * The half-day detail, in words, or null where the request covers whole days.
 *
 * Shown as a second line rather than folded into the dates, because "17 Oct"
 * and "17 Oct, afternoon only" are the same length of leave to a glance and half
 * a day apart on the payslip.
 */
function halfNote(
  request: Pick<LeaveRequestRow, "start_date" | "end_date" | "start_half" | "end_half">,
): string | null {
  if (request.start_date === request.end_date) {
    if (request.start_half === "pm" && request.end_half === "pm") return "afternoon only";
    if (request.start_half === "am" && request.end_half === "am") return "morning only";
    return null;
  }
  const parts: string[] = [];
  if (request.start_half === "pm") parts.push("from the afternoon");
  if (request.end_half === "am") parts.push("until the morning");
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Every date a range covers, both ends included. */
function datesOfRange(from: string, to: string): string[] {
  const out: string[] = [];
  const cursor = new Date(`${from}T00:00:00Z`);
  const last = Date.parse(`${to}T00:00:00Z`);
  // A request is a span of days, not of years; the ceiling is here so a bad
  // stored date cannot spin the browser.
  while (cursor.getTime() <= last && out.length < 400) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

// ---------------------------------------------------------------------------
// One list, two tables behind it
// ---------------------------------------------------------------------------

/**
 * What every request has in common, whichever table it was asked into.
 *
 * `days` is already resolved to days a person reads: leave stores half-days
 * because the half-day is the atom of time off here, and work from home stores
 * whole working days because permission to work is not granted by the morning.
 */
interface RequestRowBase {
  id: string;
  employee_id: string;
  employee_name: string | null;
  status: LeaveRequestStatus;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  /** When it was asked for, which is the order these lists are read in. */
  created_at: string;
  decided_at: string | null;
  decision_note: string | null;
  /** The half-day detail, in words, where there is any. */
  half_note: string | null;
}

/**
 * A request as these screens draw it.
 *
 * Leave and work from home are one act to the person asking and two tables
 * underneath, because approving them does opposite things: approved leave is
 * written onto the attendance grid, and approved work from home deliberately is
 * not — the day still has to be clocked
 * (`docs/work-from-home-approval.md` §3). That difference belongs to the
 * approve call, not to the history somebody reads, so both are normalised to
 * this shape and listed together.
 *
 * `source` is carried whole so each decision dialog still receives the row its
 * own endpoint answered with, rather than something reassembled from this.
 */
type RequestRow =
  | (RequestRowBase & {
      kind: "leave";
      leave_type_id: string;
      excess_unpaid: boolean;
      source: LeaveRequestRow;
    })
  | (RequestRowBase & {
      kind: "wfh";
      proposes_status: AttendanceStatus;
      source: WfhRequestRow;
    });

function fromLeaveRequest(row: LeaveRequestRow): RequestRow {
  return {
    kind: "leave",
    id: row.id,
    employee_id: row.employee_id,
    employee_name: row.employee_name,
    status: row.status,
    start_date: row.start_date,
    end_date: row.end_date,
    days: halvesToDays(row.total_halves),
    reason: row.reason,
    created_at: row.created_at,
    decided_at: row.decided_at,
    decision_note: row.decision_note,
    half_note: halfNote(row),
    leave_type_id: row.leave_type_id,
    excess_unpaid: !!row.excess_approved_as_unpaid,
    source: row,
  };
}

function fromWfhRequest(row: WfhRequestRow): RequestRow {
  return {
    kind: "wfh",
    id: row.id,
    employee_id: row.employee_id,
    employee_name: row.employee_name,
    status: row.status,
    start_date: row.start_date,
    end_date: row.end_date,
    days: row.total_days,
    reason: row.reason,
    created_at: row.created_at,
    decided_at: row.decided_at,
    decision_note: row.decision_note,
    // Whole working days, so there is no morning-or-afternoon to say.
    half_note: null,
    proposes_status: row.proposes_status ?? "wfh",
    source: row,
  };
}

// ---------------------------------------------------------------------------
// Shared reads
// ---------------------------------------------------------------------------

function useLeaveTypes() {
  return useQuery({
    queryKey: ["leave_types_all"],
    queryFn: async () => {
      const result = await api.leaveTypes.list().order("sort_order");
      if (result.error) throw new Error(result.error.message);
      /*
       * Colours resolved here, once, rather than at each swatch: a type with
       * none of its own gets one nothing else is using, so the legend never
       * shows two kinds of leave as the same square.
       */
      return withLeaveColours((result.data ?? []) as LeaveType[]);
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * The caller's own employee record.
 *
 * The only way somebody outside the payroll roles finds themselves: `/employees`
 * is refused to them, so every screen showing a person their own leave starts
 * here. Null is an ordinary answer — not everybody with a login is on the
 * payroll.
 */
function useMe() {
  return useQuery({
    queryKey: ["employee_me"],
    queryFn: async () => {
      const result = await api.employees.me();
      return result.error ? null : ((result.data ?? null) as Employee | null);
    },
  });
}

/**
 * Leave requests, scoped by the server to what this caller may see.
 *
 * `employeeId` only narrows inside that set; it cannot widen it, which is why
 * the filter is safe to drive from a picker.
 */
function useLeaveRequests(filters: { employeeId?: string } = {}) {
  return useQuery({
    queryKey: ["leave_requests", filters.employeeId ?? "all"],
    queryFn: async () => {
      const result = await api.leaveRequests
        .listRows(filters.employeeId ? { employee_id: filters.employeeId } : {})
        .all();
      if (result.error) throw new Error(result.error.message);
      return (result.data ?? []) as LeaveRequestRow[];
    },
  });
}

/**
 * Work-from-home requests, scoped by the server exactly as leave is.
 *
 * A separate read rather than a field on the leave list, because they are a
 * separate table for a reason that matters — see `RequestRow` above.
 */
function useWfhRequests(filters: { employeeId?: string } = {}) {
  return useQuery({
    queryKey: ["wfh_requests", filters.employeeId ?? "all"],
    queryFn: async () => {
      const result = await api.wfhRequests
        .listRows(filters.employeeId ? { employee_id: filters.employeeId } : {})
        .all();
      if (result.error) throw new Error(result.error.message);
      return (result.data ?? []) as WfhRequestRow[];
    },
  });
}

/**
 * Both kinds of request, merged and newest-asked first.
 *
 * One hook rather than two reads at each screen, so a list cannot show leave
 * that is current beside work-from-home rows a render behind it. `leaveRows` is
 * handed back as well for the year calendar, which is drawn from leave alone:
 * nothing is written to the grid for work from home, and outlining those dates
 * as pending leave would say somebody is away when they are working.
 *
 * The order is `created_at`, not the dates asked for. The list is a record of
 * asking, and the thing somebody opens it for — "what came of the request I put
 * in yesterday" — is found by when it was filed, not by when the days fall. The
 * two orders disagree exactly where it matters: a request filed this morning for
 * next March sorts to the top by when it was asked and sinks into the middle of
 * the year by its start date, which is where it goes missing on a paged list.
 * `start_date` breaks a tie, so two requests filed in the same second still have
 * a stable order rather than whatever the merge happened to produce.
 */
function useRequests(filters: { employeeId?: string } = {}) {
  const leave = useLeaveRequests(filters);
  const wfh = useWfhRequests(filters);

  const rows = useMemo(() => {
    const merged = [
      ...(leave.data ?? []).map(fromLeaveRequest),
      ...(wfh.data ?? []).map(fromWfhRequest),
    ];
    merged.sort(
      (a, b) =>
        b.created_at.localeCompare(a.created_at) || b.start_date.localeCompare(a.start_date),
    );
    return merged;
  }, [leave.data, wfh.data]);

  return {
    rows,
    leaveRows: leave.data ?? [],
    isPending: leave.isPending || wfh.isPending,
  };
}

function useBalances(employeeId: string | undefined, year: number) {
  return useQuery({
    queryKey: ["leave_balances", employeeId, year],
    enabled: !!employeeId,
    queryFn: async () => {
      const result = await api.employees.leaveBalances(employeeId!, year);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

/** Everything a decision screen invalidates once a request has been decided. */
function invalidateLeave(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["leave_requests"] });
  void qc.invalidateQueries({ queryKey: ["wfh_requests"] });
  void qc.invalidateQueries({ queryKey: ["leave_balances"] });
  void qc.invalidateQueries({ queryKey: ["leave_year_grid"] });
  void qc.invalidateQueries({ queryKey: ["attendance"] });
  /* The bell is drawn from the same pending requests this just decided one of,
     so it has to be told — otherwise the count sits a minute behind the screen
     that cleared it, which reads as the decision not having taken. */
  void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
}

// ---------------------------------------------------------------------------
// The employee's own screen
// ---------------------------------------------------------------------------

/**
 * What somebody asking for time off needs: how much they have, what they have
 * asked for, and what came of each ask.
 *
 * Every status is on one list rather than split across "waiting" and "decided"
 * tables, because the question the list answers is "what happened to the days I
 * asked for in March" — and a request that was rejected and re-asked reads as a
 * sequence only when the two are next to each other. The filter above it is how
 * a long year is narrowed, and it carries counts so an empty state is legible as
 * "none of those" rather than "nothing at all".
 */
function MyLeave() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const me = useMe();
  const types = useLeaveTypes();

  return (
    <div className="space-y-6">
      <PageHeader
        title="My leave"
        description="What you have left, what you have asked for, and what came of it."
        actions={
          me.data ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {/* Neither word, because the dialog asks for either and work
                  from home is not leave — it is a day worked. "Request leave"
                  is where somebody looking for it concludes there is nowhere
                  to ask; naming both is a sentence on a button. */}
              New request
            </Button>
          ) : undefined
        }
      />

      <OwnLeave me={me.data ?? null} loading={me.isPending} />

      {creating && me.data && (
        <RequestDialog
          types={types.data ?? []}
          me={me.data}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidateLeave(qc);
          }}
        />
      )}
    </div>
  );
}

/**
 * One person's own leave, as the person themselves reads it.
 *
 * Written as a block rather than as a page so an approver gets it too: being
 * able to decide the team's leave does not stop somebody having their own, and
 * before this the approving roles had no screen showing their balance or their
 * own history at all. It is also the only way to see what the team sees — a
 * super administrator cannot sign in as somebody else to check.
 *
 * Every status is on one list, in one table, rather than split across a waiting
 * table and a decided one. The question this list answers is "what happened to
 * the days I asked for in March", and a request that was rejected and asked
 * again reads as a sequence only when the two are next to each other. What was
 * asked and what was answered sit in the same row: the reason under the dates,
 * the decision and its note under the status.
 */
function OwnLeave({ me, loading }: { me: Employee | null; loading: boolean }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LeaveRequestStatus | "all">("all");
  const [year, setYear] = useState(thisYear());

  const types = useLeaveTypes();
  const requests = useRequests();
  const cancel = useCancelRequest(qc);

  const typeById = useMemo(() => new Map((types.data ?? []).map((t) => [t.id, t])), [types.data]);

  /*
   * Own rows only. The server scopes the list to what the caller may see, and
   * for somebody outside the approving roles that is themselves *plus anybody
   * whose record names them as manager* — so without this a block called "my
   * leave" could list a colleague's time off, in a table with no name column to
   * say whose it was. Deciding for a report is the approver's screen.
   */
  const rows = requests.rows.filter((r) => r.employee_id === me?.id);
  const counts = useMemo(() => countByStatus(rows), [rows]);
  const shown = status === "all" ? rows : rows.filter((r) => r.status === status);

  /*
   * A year of asking is more rows than belong on one screen, and the calendar
   * below is what the list would otherwise be scrolled past to reach. The status
   * tab is the reset key: switching from "All 40" to "Rejected 2" while on page
   * 3 would show an empty table and read as a tab with nothing in it, when the
   * count on the tab itself says otherwise.
   */
  const pager = usePagination({ pageSize: 10, resetKey: status });

  return (
    <div className="space-y-6">
      <NotOnTheRegister me={me} loading={loading} />

      <BalanceStrip employeeId={me?.id} year={thisYear()} />

      <section className="space-y-3">
        <StatusFilter value={status} counts={counts} total={rows.length} onChange={setStatus} />

        <RequestsTable
          rows={shown}
          typeById={typeById}
          loading={requests.isPending}
          pagination={pager}
          empty={
            rows.length === 0
              ? "You have not asked for any leave or work from home yet."
              : "No request of yours is in that state."
          }
          actions={(request) =>
            ["pending", "approved"].includes(request.status) ? (
              <Button
                size="sm"
                variant="outline"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(request)}
              >
                {request.status === "pending" ? "Withdraw" : "Cancel"}
              </Button>
            ) : null
          }
        />
      </section>

      {me && (
        /* Leave only: nothing is written to the grid for work from home, so
           outlining those dates as pending would say somebody is away on days
           they are working. */
        <LeaveYearCalendar
          employeeId={me.id}
          year={year}
          onYearChange={setYear}
          requests={requests.leaveRows.filter((r) => r.employee_id === me.id)}
          typeById={typeById}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The approver's screen
// ---------------------------------------------------------------------------

/**
 * What somebody deciding needs: a queue, and the rest of the register behind it.
 *
 * The queue is first and alone on its tab, because a pending request is the only
 * thing on this page that is waiting on the person reading it — mixed into a
 * table of everything it stops being a queue. The other two tabs are the
 * register: everything that has been asked, and one person's year drawn as the
 * calendar it actually is.
 */
function ApproverLeave() {
  const qc = useQueryClient();
  const { hasAny } = useAuth();
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState<RequestRow | null>(null);
  const [status, setStatus] = useState<LeaveRequestStatus | "all">("all");
  const [employeeId, setEmployeeId] = useState("");
  const [calendarPerson, setCalendarPerson] = useState("");
  const [year, setYear] = useState(thisYear());
  const [search, setSearch] = useState("");
  // Both lists are read whole and paged here; a new filter starts at page 1.
  const queuePager = usePagination({ resetKey: search });
  const allPager = usePagination({ resetKey: { search, status, employeeId } });

  const tab = useLeaveTab();
  const me = useMe();
  const types = useLeaveTypes();
  const requests = useRequests();

  const typeById = useMemo(() => new Map((types.data ?? []).map((t) => [t.id, t])), [types.data]);

  /*
   * The search narrows both request lists by who asked, the kind of leave and
   * what was written on the request. Every request is already in the browser,
   * so it reads the whole set rather than one page of it.
   */
  const needle = search.trim().toLowerCase();
  const matches = (r: RequestRow) =>
    !needle ||
    [
      r.employee_name,
      r.reason,
      r.decision_note,
      r.kind === "leave" ? typeById.get(r.leave_type_id)?.name : "Work from home",
    ].some((text) => text?.toLowerCase().includes(needle));

  const rows = requests.rows;
  const pending = rows.filter((r) => r.status === "pending");

  const filtered = rows.filter(
    (r) =>
      (status === "all" || r.status === status) &&
      (!employeeId || r.employee_id === employeeId) &&
      matches(r),
  );
  const counts = useMemo(
    () => countByStatus(employeeId ? rows.filter((r) => r.employee_id === employeeId) : rows),
    [rows, employeeId],
  );

  const cancel = useCancelRequest(qc);

  /*
   * An administrator decides leave here; they do not take it. The accounts in
   * ADMIN_ROLES run the system rather than sit on the payroll, so asking for
   * their own days off is a button that either does nothing useful or files a
   * request against somebody else's employee record. The managers and owners
   * who also approve are employees, and keep both halves of the screen.
   */
  const asksForOwn = !hasAny(ADMIN_ROLES);

  /** The person the calendar is about, defaulting to whoever is first in the queue. */
  const calendarFor = calendarPerson || pending[0]?.employee_id || me.data?.id || "";

  const review = (request: RequestRow) => (
    <Button size="sm" onClick={() => setDeciding(request)}>
      Review
    </Button>
  );

  return (
    // Fills the window like a list page: the request lists scroll inside their
    // own table cards, and the calendar and own-leave tabs scroll in place.
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Leave"
        description={
          asksForOwn
            ? "Decide the leave and the work-from-home the team asks for, and ask for your own."
            : "Decide the leave and the work-from-home the team asks for."
        }
        actions={
          asksForOwn && me.data ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {/* Neither word, because the dialog asks for either and work
                  from home is not leave — it is a day worked. "Request leave"
                  is where somebody looking for it concludes there is nowhere
                  to ask; naming both is a sentence on a button. */}
              New request
            </Button>
          ) : undefined
        }
      />

      <Tabs
        value={tab.value}
        onValueChange={tab.onChange}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        {/* The tabs lead, with the search beside them for the two request
            lists; the filters for "All requests" come underneath, in its tab. */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <TabsList className="shrink-0">
            <TabsTrigger value="queue" className="gap-2">
              <Inbox className="h-3.5 w-3.5" />
              Awaiting your decision
              {pending.length > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
                >
                  {pending.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              <History className="h-3.5 w-3.5" />
              All requests
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2">
              <CalendarDays className="h-3.5 w-3.5" />
              Calendar
            </TabsTrigger>
            {/* Deciding for the team does not stop you having your own, and this
              is also the only way to see the screen the team is given. */}
            {asksForOwn && (
              <TabsTrigger value="mine" className="gap-2">
                <User className="h-3.5 w-3.5" />
                My leave
              </TabsTrigger>
            )}
          </TabsList>
          {(tab.value === "queue" || tab.value === "all") && (
            <ListSearch
              value={search}
              onChange={setSearch}
              placeholder="Search name, leave type or reason…"
            />
          )}
        </div>

        <TabsContent
          value="queue"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          <RequestsTable
            pagination={queuePager}
            rows={pending.filter(matches)}
            typeById={typeById}
            showEmployee
            loading={requests.isPending}
            empty={
              needle && pending.length ? "Nothing matches that." : "Nothing is waiting on you."
            }
            actions={review}
          />
        </TabsContent>

        <TabsContent
          value="all"
          className="mt-0 hidden min-h-0 flex-1 flex-col gap-3 data-[state=active]:flex"
        >
          <ListToolbar className="mb-0 shrink-0">
            <AttendancePersonSelect
              value={employeeId}
              onChange={setEmployeeId}
              placeholder="Everybody"
              clearable
              clearLabel="Everybody"
              className="w-72"
            />
            <StatusFilter value={status} counts={counts} total={counts.all} onChange={setStatus} />
          </ListToolbar>

          <RequestsTable
            pagination={allPager}
            rows={filtered}
            typeById={typeById}
            showEmployee
            loading={requests.isPending}
            empty={rows.length === 0 ? "Nothing has been asked for yet." : "Nothing matches that."}
            actions={(request) =>
              request.status === "pending" ? (
                review(request)
              ) : request.status === "approved" ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate(request)}
                >
                  Cancel
                </Button>
              ) : null
            }
          />
        </TabsContent>

        <TabsContent value="calendar" className="mt-0 min-h-0 flex-1 space-y-4 overflow-y-auto">
          <div className="w-full max-w-72 space-y-2">
            <Label>Whose year</Label>
            <AttendancePersonSelect
              value={calendarFor}
              onChange={setCalendarPerson}
              placeholder="Pick somebody"
            />
          </div>

          {calendarFor ? (
            <>
              <BalanceStrip employeeId={calendarFor} year={year} />
              <LeaveYearCalendar
                employeeId={calendarFor}
                year={year}
                onYearChange={setYear}
                requests={requests.leaveRows.filter((r) => r.employee_id === calendarFor)}
                typeById={typeById}
              />
            </>
          ) : (
            <p className="text-muted-foreground text-sm">Pick somebody to see their year.</p>
          )}
        </TabsContent>

        {asksForOwn && (
          <TabsContent value="mine" className="mt-0 min-h-0 flex-1 overflow-y-auto">
            <OwnLeave me={me.data ?? null} loading={me.isPending} />
          </TabsContent>
        )}
      </Tabs>

      {creating && asksForOwn && me.data && (
        <RequestDialog
          types={types.data ?? []}
          me={me.data}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidateLeave(qc);
          }}
        />
      )}

      {/* Two dialogs, because the two decisions are not the same decision. A
          leave approval is weighed against a balance and writes the days onto
          the grid; a work-from-home approval has no balance to weigh and writes
          nothing. One dialog with half its controls hidden would imply they are
          the same act. */}
      {deciding?.kind === "leave" && (
        <DecisionDialog
          request={deciding.source}
          type={typeById.get(deciding.leave_type_id) ?? null}
          typeById={typeById}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            invalidateLeave(qc);
          }}
        />
      )}

      {deciding?.kind === "wfh" && (
        <WfhDecisionDialog
          request={deciding}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            invalidateLeave(qc);
          }}
        />
      )}
    </div>
  );
}

/**
 * Cancelling, which is the same act from either screen.
 *
 * An employee may withdraw their own; an approver may take back somebody else's.
 * The server applies that rule — an approved request's half-days come back off
 * the attendance grid, which is what credits the allowance, and days inside a
 * closed pay period are refused.
 */
function useCancelRequest(qc: ReturnType<typeof useQueryClient>) {
  return useMutation({
    mutationFn: async (request: RequestRow) => {
      const result =
        request.kind === "wfh"
          ? await api.wfhRequests.cancel(request.id)
          : await api.leaveRequests.cancel(request.id);
      if (result.error) throw new Error(result.error.message);
      return request;
    },
    onSuccess: (request) => {
      notify.success(
        request.status === "pending"
          ? "Request withdrawn"
          : request.kind === "wfh"
            ? /* Nothing comes back off the grid — approval never put anything
                 there — and a day already clocked under the permission was
                 judged when it happened. */
              "Cancelled. Days already worked under it are unaffected."
            : "Leave cancelled and the days released",
      );
      invalidateLeave(qc);
    },
    onError: (error: Error) => notify.error(error.message),
  });
}

function countByStatus(rows: RequestRow[]): Record<string, number> {
  const counts: Record<string, number> = { all: rows.length };
  for (const row of rows) counts[row.status] = (counts[row.status] ?? 0) + 1;
  return counts;
}

// ---------------------------------------------------------------------------
// Pieces both screens use
// ---------------------------------------------------------------------------

function StatusBadge({ request }: { request: RequestRow }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge className={cn("font-normal", STATUS_TONE[request.status])} variant="secondary">
        {STATUS_LABEL[request.status] ?? request.status}
      </Badge>
      {request.kind === "leave" && request.excess_unpaid && (
        <Badge
          variant="outline"
          className="border-orange-400 text-[10px] text-orange-800 dark:border-orange-500/60 dark:text-orange-300"
        >
          excess unpaid
        </Badge>
      )}
    </span>
  );
}

/**
 * The status filter, with what each one would show.
 *
 * The counts are the point: "Rejected 0" and a missing tab are different
 * sentences, and only the first of them answers "was anything of mine turned
 * down this year".
 */
function StatusFilter({
  value,
  counts,
  total,
  onChange,
}: {
  value: LeaveRequestStatus | "all";
  counts: Record<string, number>;
  total: number;
  onChange: (next: LeaveRequestStatus | "all") => void;
}) {
  const option = (key: LeaveRequestStatus | "all", label: string, count: number) => (
    <Button
      key={key}
      size="sm"
      variant={value === key ? "default" : "outline"}
      className="h-8"
      onClick={() => onChange(key)}
    >
      {label}
      <span className="ml-1.5 tabular-nums opacity-70">{count}</span>
    </Button>
  );

  return (
    <div className="flex flex-wrap gap-2">
      {option("all", "All", total)}
      {FILTER_STATUSES.map((status) =>
        option(status, STATUS_LABEL[status] ?? status, counts[status] ?? 0),
      )}
    </div>
  );
}

function LeaveTypeChip({ type }: { type: LeaveType | undefined }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold text-white"
        style={{ backgroundColor: type ? leaveTypeColour(type) : "#475569" }}
      >
        {type?.code ?? "?"}
      </span>
      {type?.name ?? "Unknown"}
    </span>
  );
}

/**
 * What a row is, in a column: the leave type, or the arrangement.
 *
 * Work from home gets the grid's own amber `W` rather than a leave swatch,
 * because that is what the attendance screens paint those days and a second
 * visual language for the same thing would be one to learn for nothing. The
 * label says "working", never "leave", which is the distinction the whole table
 * is carrying.
 */
function RequestKindChip({
  request,
  typeById,
}: {
  request: RequestRow;
  typeById: Map<string, LeaveType>;
}) {
  if (request.kind === "leave") {
    return <LeaveTypeChip type={typeById.get(request.leave_type_id)} />;
  }

  return (
    <span className="flex items-center gap-1.5">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-amber-400 text-[10px] font-semibold text-amber-950">
        W
      </span>
      {REMOTE_STATUS_LABEL[request.proposes_status] ?? "Working from home"}
    </span>
  );
}

/**
 * One table, drawn the same way wherever requests are listed.
 *
 * The employee column appears only where more than one person's requests can be
 * on the list — a column of your own name repeated down your own history is not
 * information.
 */
function RequestsTable({
  rows,
  typeById,
  showEmployee = false,
  loading = false,
  empty,
  actions,
  pagination,
}: {
  rows: RequestRow[];
  typeById: Map<string, LeaveType>;
  showEmployee?: boolean;
  loading?: boolean;
  empty: string;
  actions?: (request: RequestRow) => React.ReactNode;
  /**
   * A pager for the list. Both request tables are read whole — the rows are a
   * merge of two tables, so there is no one query to page server-side — and the
   * slicing therefore happens here, over the rows already filtered. Left out,
   * the table draws every row it is handed, which is what the short lists
   * inside a decision dialog want.
   */
  pagination?: ListPagination;
}) {
  const columns = showEmployee ? 7 : 6;

  const shown = pagination
    ? rows.slice(pagination.offset, pagination.offset + pagination.pageSize)
    : rows;

  // Sized like a list page's table card: it takes the room it needs up to what
  // the page leaves, and the rows scroll under pinned headings.
  return (
    <div className="bg-card flex min-h-0 flex-col overflow-hidden rounded-md border">
      <Table containerClassName="min-h-0 flex-1">
        <TableHeader sticky>
          <TableRow>
            {showEmployee && <TableHead>Employee</TableHead>}
            <TableHead>Type</TableHead>
            <TableHead>Dates</TableHead>
            <TableHead className="text-right">Days</TableHead>
            {/* The column the list is ordered by. Without it the order reads as
                arbitrary — the dates column runs neither up nor down — and on a
                paged list that is the difference between "the newest are on page
                one" and "the rows are in no order I can see". */}
            <TableHead>Asked on</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns} className="text-muted-foreground py-8 text-center">
                {loading ? "Loading…" : empty}
              </TableCell>
            </TableRow>
          ) : (
            shown.map((request) => {
              const note = request.half_note;
              return (
                <TableRow key={`${request.kind}-${request.id}`}>
                  {showEmployee && (
                    <TableCell className="font-medium">{request.employee_name ?? "—"}</TableCell>
                  )}
                  <TableCell>
                    <RequestKindChip request={request} typeById={typeById} />
                  </TableCell>
                  <TableCell>
                    <div className="tabular-nums">{spanLabel(request)}</div>
                    {note && <div className="text-muted-foreground text-xs">{note}</div>}
                    {/* What they said when they asked, next to what was decided
                        about it — the two halves of the same conversation. */}
                    {request.reason && (
                      <div className="text-muted-foreground mt-1 max-w-64 text-xs italic">
                        “{request.reason}”
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{request.days}</TableCell>
                  <TableCell className="text-muted-foreground tabular-nums whitespace-nowrap">
                    {dayLabel(request.created_at.slice(0, 10))}
                  </TableCell>
                  <TableCell>
                    <StatusBadge request={request} />
                    {request.decided_at && request.status !== "pending" && (
                      <div className="text-muted-foreground mt-1 text-xs">
                        {dayLabel(request.decided_at.slice(0, 10))}
                      </div>
                    )}
                    {request.decision_note && (
                      <div className="mt-1 max-w-64 text-xs">“{request.decision_note}”</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{actions?.(request)}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Inside the border, so the count and the controls read as part of the
          same card as the rows they describe. */}
      {pagination && (
        <TablePagination
          pagination={pagination}
          total={rows.length}
          loading={loading}
          unit="request"
        />
      )}
    </div>
  );
}

/**
 * What one person has left, per type.
 *
 * Entitlements are set on the employee record, under Administration — they are
 * part of somebody's terms, like their salary, not something decided on the
 * screen where leave is requested. What belongs here is the figure a person
 * needs before asking, and the figure an approver needs before granting.
 */
function BalanceStrip({
  employeeId,
  year,
  highlightTypeId,
}: {
  employeeId: string | undefined;
  year: number;
  /** The type a decision is about, so it reads first and reads loudest. */
  highlightTypeId?: string;
}) {
  const balances = useBalances(employeeId, year);

  const rows = (balances.data?.balances ?? []).filter((b) => b.consumes_allowance);
  if (!employeeId || rows.length === 0) return null;

  const ordered = highlightTypeId
    ? [...rows].sort((a, b) =>
        a.leave_type_id === highlightTypeId ? -1 : b.leave_type_id === highlightTypeId ? 1 : 0,
      )
    : rows;

  return (
    <div className="flex flex-wrap gap-3">
      {ordered.map((balance) => (
        <div
          key={balance.leave_type_id}
          className={cn(
            "min-w-36 rounded-md border px-3 py-2",
            balance.leave_type_id === highlightTypeId && "border-primary ring-1 ring-primary",
          )}
        >
          <div className="text-muted-foreground text-xs">{balance.name}</div>
          <div
            className={cn(
              "text-2xl font-semibold tabular-nums",
              balance.remaining_days < 0 && "text-red-600 dark:text-red-400",
            )}
          >
            {balance.remaining_days}
          </div>
          <div className="text-muted-foreground text-xs">
            of {balance.total_days} left in {year}
            {balance.booked_days > 0 && ` · ${balance.booked_days} booked`}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Said out loud when a login matches no employee record.
 *
 * The match is by email address, and it is what every figure on this page hangs
 * off — the balance, the history, the person a request is raised for. Without it
 * the screen is empty for a reason nobody can see from the screen, and the one
 * thing that fixes it is an administrator putting the address on the employee
 * record. So it says that, rather than showing a working page with nothing on it.
 */
function NotOnTheRegister({ me, loading }: { me: Employee | null; loading: boolean }) {
  if (me || loading) return null;

  return (
    <Alert>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>No employee record is linked to this login</AlertTitle>
      <AlertDescription>
        Leave is kept against an employee record, and logins are matched to one by email address.
        Nothing matches yours, so there is no balance to show and nothing to request against. An
        administrator can put your address on your record under Employees.
      </AlertDescription>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// The year, as a calendar
// ---------------------------------------------------------------------------

/**
 * One person's leave year, twelve months at a time.
 *
 * Drawn from the attendance grid rather than from the requests, because the grid
 * is what approving a request writes to and what the pay run reads: a day a
 * manager corrected by hand shows here without a second write anywhere, and an
 * approval whose days never landed does not show as leave that was never taken.
 *
 * Pending requests are the one thing the grid cannot know — nothing has been
 * written for them yet — so they are overlaid as dashed outlines. That is the
 * whole reason an approver wants this on screen: the question in front of them
 * is not "how much is left", which the tiles above already answer, but "do these
 * dates land on top of something".
 */
function LeaveYearCalendar({
  employeeId,
  year,
  onYearChange,
  requests,
  typeById,
  highlight,
}: {
  employeeId: string;
  year: number;
  onYearChange: (year: number) => void;
  /** This employee's requests, for the pending overlay and the day's tooltip. */
  requests: LeaveRequestRow[];
  typeById: Map<string, LeaveType>;
  /** Dates of the request being decided, outlined so they are findable. */
  highlight?: Set<string>;
}) {
  const grid = useQuery({
    queryKey: ["leave_year_grid", employeeId, year],
    queryFn: async () => {
      const result = await api.attendance.grid(`${year}-01-01`, `${year}-12-31`, employeeId);
      if (result.error) throw new Error(result.error.message);
      return result.data?.employees?.[0] ?? null;
    },
  });

  const byDate = useMemo(
    () => new Map((grid.data?.days ?? []).map((d) => [d.date, d])),
    [grid.data],
  );

  /** Pending dates, and which type each was asked for as. */
  const pendingByDate = useMemo(() => {
    const map = new Map<string, LeaveRequestRow>();
    for (const request of requests) {
      if (request.status !== "pending") continue;
      if (request.employee_id !== employeeId) continue;
      for (const date of datesOfRange(request.start_date, request.end_date)) {
        if (date.slice(0, 4) === String(year)) map.set(date, request);
      }
    }
    return map;
  }, [requests, employeeId, year]);

  /** Only the types this year actually contains, so the legend is about this year. */
  const legend = useMemo(() => {
    const ids = new Set<string>();
    for (const day of grid.data?.days ?? []) {
      if (day.am_leave_type_id) ids.add(day.am_leave_type_id);
      if (day.pm_leave_type_id) ids.add(day.pm_leave_type_id);
    }
    for (const request of pendingByDate.values()) ids.add(request.leave_type_id);
    return [...ids].map((id) => typeById.get(id)).filter((t): t is LeaveType => !!t);
  }, [grid.data, pendingByDate, typeById]);

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => onYearChange(year - 1)}>
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">The year before</span>
        </Button>
        <div className="min-w-16 text-center text-lg font-semibold tabular-nums">{year}</div>
        <Button variant="outline" size="icon" onClick={() => onYearChange(year + 1)}>
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">The year after</span>
        </Button>
        {grid.isPending && <span className="text-muted-foreground text-sm">Loading…</span>}
        {grid.isError && (
          <span className="text-muted-foreground text-sm">This year could not be read.</span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {MONTH_NAMES.map((name, month) => (
          <MonthGrid
            key={name}
            year={year}
            month={month}
            byDate={byDate}
            pendingByDate={pendingByDate}
            typeById={typeById}
            highlight={highlight}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        {legend.map((type) => (
          <span key={type.id} className="flex items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-sm"
              style={{ backgroundColor: leaveTypeColour(type) }}
            />
            {type.name}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="flex h-1 w-4 overflow-hidden rounded-full">
            <span className="bg-foreground/70 flex-1" />
            <span className="bg-muted flex-1" />
          </span>
          Half a day, or two halves of different types
        </span>
        {pendingByDate.size > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="border-foreground/60 h-3 w-3 rounded-sm border-2 border-dashed" />
            Waiting on a decision
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <span className="bg-muted h-3 w-3 rounded-sm border" />
          Weekend or public holiday
        </span>
      </div>
    </section>
  );
}

/** The colour a half-day is drawn in, or null where it is not leave. */
function halfColour(
  status: string | undefined,
  leaveTypeId: string | null | undefined,
  typeById: Map<string, LeaveType>,
): string | null {
  if (status !== "leave") return null;
  const type = leaveTypeId ? typeById.get(leaveTypeId) : undefined;
  return type ? leaveTypeColour(type) : "#475569";
}

/**
 * One month as a grid of days.
 *
 * A day is drawn as its two halves, because a morning of leave and an afternoon
 * at work is one square and two answers — the diagonal split is what keeps the
 * half-day visible at this size, where two separate bands would be a smear.
 */
function MonthGrid({
  year,
  month,
  byDate,
  pendingByDate,
  typeById,
  highlight,
}: {
  year: number;
  month: number;
  byDate: Map<string, AttendanceGridDay>;
  pendingByDate: Map<string, LeaveRequestRow>;
  typeById: Map<string, LeaveType>;
  highlight?: Set<string>;
}) {
  const total = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const lead = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const today = todayIso();

  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from(
      { length: total },
      (_, i) => `${year}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`,
    ),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="overflow-hidden rounded-md border">
      <div className="bg-muted/50 border-b px-2 py-1 text-xs font-medium">{MONTH_NAMES[month]}</div>

      <div className="text-muted-foreground grid grid-cols-7 text-center text-[10px]">
        {WEEKDAY_INITIALS.map((initial, index) => (
          <div key={index} className="py-1">
            {initial}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px p-1">
        {cells.map((date, index) => {
          if (!date) return <div key={`pad-${index}`} className="h-8" />;

          const day = byDate.get(date);
          const pending = pendingByDate.get(date);
          const pendingType = pending ? typeById.get(pending.leave_type_id) : undefined;

          const am = halfColour(day?.am_status, day?.am_leave_type_id, typeById);
          const pm = halfColour(day?.pm_status, day?.pm_leave_type_id, typeById);

          const offDuty =
            day?.am_status === "holiday" || day?.am_status === "non_working" || !!day?.holiday_name;

          /*
           * A whole day of one type is filled, because that is what makes a year
           * scannable at this size. Anything else — a morning of leave, or two
           * halves of different types — is a two-segment bar under the date
           * instead. Filling half a square would put the date over the colour on
           * one side and over the background on the other, and no single text
           * colour is readable on both.
           */
          const wholeDay = am && pm && am === pm ? am : null;
          const partDay = !wholeDay && !!(am || pm);

          return (
            <div
              key={date}
              title={dayTitle(date, day, pending, typeById)}
              style={{
                backgroundColor: wholeDay ?? undefined,
                borderColor: pending ? (pendingType?.colour ?? undefined) : undefined,
              }}
              className={cn(
                "flex h-8 flex-col items-center justify-center gap-0.5 rounded-sm text-[10px] tabular-nums",
                offDuty && !wholeDay && !partDay && "bg-muted text-muted-foreground",
                wholeDay && "font-semibold text-white",
                pending && "border-2 border-dashed",
                pending && !pendingType?.colour && "border-foreground/50",
                highlight?.has(date) && "ring-primary ring-2 ring-offset-1",
                date === today && "underline underline-offset-2",
              )}
            >
              <span className="leading-none">{Number(date.slice(8))}</span>
              {partDay && (
                <span className="flex h-1 w-4 overflow-hidden rounded-full">
                  <span className="flex-1" style={{ backgroundColor: am ?? "transparent" }} />
                  <span className="flex-1" style={{ backgroundColor: pm ?? "transparent" }} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The hover text for one square — what it is, in a sentence. */
function dayTitle(
  date: string,
  day: AttendanceGridDay | undefined,
  pending: LeaveRequestRow | undefined,
  typeById: Map<string, LeaveType>,
): string {
  const parts = [dayLabel(date)];
  if (day?.holiday_name) parts.push(day.holiday_name);

  const half = (label: string, status: string | undefined, typeId: string | null | undefined) => {
    if (status !== "leave") return;
    parts.push(`${label}: ${typeById.get(typeId ?? "")?.name ?? "leave"}`);
  };
  half("Morning", day?.am_status, day?.am_leave_type_id);
  half("Afternoon", day?.pm_status, day?.pm_leave_type_id);

  if (pending) {
    parts.push(
      `Waiting: ${typeById.get(pending.leave_type_id)?.name ?? "leave"}` +
        (pending.employee_name ? ` for ${pending.employee_name}` : ""),
    );
  }
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Asking
// ---------------------------------------------------------------------------

function RequestDialog({
  types,
  me,
  onClose,
  onSaved,
}: {
  types: LeaveType[];
  /** The person the request is for, which is always the person making it. */
  me: Employee;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<LeaveRequestDraft>({
    /*
     * Never asked. A login resolves to one employee record by email address, so
     * "who is this for" has exactly one answer and the form already holds it —
     * and an approver raising leave against the wrong name was the only thing a
     * picker here ever made possible. Recording somebody else's absence is a
     * different act with a different screen: it is written straight onto the
     * attendance grid, where it belongs.
     */
    employee_id: me.id,
    leave_type_id: "",
    start_date: todayIso(),
    end_date: todayIso(),
    start_half: "am",
    end_half: "pm",
  });

  /**
   * Which arrangement, where the dates are away from the office.
   *
   * `wfh` for almost all of them. The other three are here because the server's
   * `proposes_status` accepts them and they are the same permission — without
   * the control a week at a client could only be asked for as working from
   * home, and the grid would then say so for a year.
   */
  const [proposesStatus, setProposesStatus] = useState<AttendanceStatus>("wfh");

  /*
   * Work from home rides in the type picker as a sentinel, so `leave_type_id`
   * holds either a real type or `WFH_OPTION` and one control answers "what am I
   * asking for". The two go to different endpoints below.
   */
  const isWfh = form.leave_type_id === WFH_OPTION;

  const set = <K extends keyof LeaveRequestDraft>(key: K, value: LeaveRequestDraft[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      if (isWfh) {
        /*
         * A different table, not a leave type with the flags turned off. It
         * consumes no allowance and is never an absence, and — the part worth
         * reading twice — approving it writes nothing onto the attendance grid.
         * The day still has to be clocked. `docs/work-from-home-approval.md` §3.
         *
         * Whole days only: the halves above are not sent, because permission to
         * work from home is not granted by the morning.
         */
        const result = await api.wfhRequests.create({
          employee_id: form.employee_id,
          start_date: form.start_date,
          end_date: form.end_date,
          proposes_status: proposesStatus,
          reason: form.reason?.trim() || null,
        });
        if (result.error) throw new Error(result.error.message);
        return "wfh" as const;
      }

      const result = await api.leaveRequests.create(form);
      if (result.error) throw new Error(result.error.message);
      return "leave" as const;
    },
    onSuccess: (kind) => {
      notify.success(
        kind === "wfh"
          ? "Work from home requested. Once approved, the days still have to be clocked."
          : "Leave requested",
      );
      onSaved();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isWfh ? "Request work from home" : "Request leave"}</DialogTitle>
          <DialogDescription>
            Weekends and public holidays inside the dates are not counted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Requesting as <strong>{me.full_name}</strong>.
          </p>

          <div className="space-y-2">
            <Label required>What are you asking for</Label>
            <Select value={form.leave_type_id} onValueChange={(v) => set("leave_type_id", v)}>
              <SelectTrigger>
                <SelectValue placeholder="Time off, or working from home" />
              </SelectTrigger>
              <SelectContent>
                {types
                  .filter((t) => t.active && t.self_bookable)
                  .map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.code} — {type.name}
                    </SelectItem>
                  ))}
                {/* Last, and after the leave types, because it is not one of
                    them — it is the other thing this form can ask for. */}
                <SelectItem value={WFH_OPTION}>W — Work from home</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isWfh && (
            <>
              <Alert>
                <Laptop className="h-4 w-4" />
                <AlertTitle>This is a day worked, not time off</AlertTitle>
                <AlertDescription>
                  It comes out of no allowance and counts as no absence. Once it is approved you
                  still clock in and out on those days as you would in the office — the approval
                  only means the day reads as working from home rather than being queried.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>Where you will be</Label>
                <Select
                  value={proposesStatus}
                  onValueChange={(v) => setProposesStatus(v as AttendanceStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REMOTE_WORKING_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {REMOTE_STATUS_LABEL[status] ?? status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="start_date" required>
                From
              </Label>
              <Input
                id="start_date"
                type="date"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
              />
              {/* Whole days only for work from home: the request carries no
                  halves, so offering them here would be a control with nothing
                  behind it. */}
              {!isWfh && (
                <Select
                  value={form.start_half ?? "am"}
                  onValueChange={(v) => set("start_half", v as "am" | "pm")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="am">From the morning</SelectItem>
                    <SelectItem value="pm">From the afternoon</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_date" required>
                To
              </Label>
              <Input
                id="end_date"
                type="date"
                value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)}
              />
              {!isWfh && (
                <Select
                  value={form.end_half ?? "pm"}
                  onValueChange={(v) => set("end_half", v as "am" | "pm")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="am">Until the morning</SelectItem>
                    <SelectItem value="pm">Until the afternoon</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              value={form.reason ?? ""}
              onChange={(e) => set("reason", e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.leave_type_id}>
            Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Deciding
// ---------------------------------------------------------------------------

/**
 * Approving or rejecting, with the employee's year on the same screen.
 *
 * The decision is never only about the request. It is about the request against
 * what the person has left, what they have already booked, and where these dates
 * fall — which used to mean opening the attendance screen in another tab, and in
 * practice meant deciding without looking. All three are here: the balance of
 * every type on the left, their requests and their year on the right.
 *
 * The balance check itself runs on the server, and a refusal comes back as a 409
 * naming the shortfall. That refusal is shown with the one thing that resolves it
 * — taking the excess as unpaid — as an explicit tick rather than a retry that
 * silently succeeds.
 */
function DecisionDialog({
  request,
  type,
  typeById,
  onClose,
  onDecided,
}: {
  request: LeaveRequestRow;
  type: LeaveType | null;
  typeById: Map<string, LeaveType>;
  onClose: () => void;
  onDecided: () => void;
}) {
  const [note, setNote] = useState("");
  const [asUnpaid, setAsUnpaid] = useState(false);
  const [shortfall, setShortfall] = useState<string | null>(null);
  const [year, setYear] = useState(Number(request.start_date.slice(0, 4)));

  const balances = useBalances(request.employee_id, Number(request.start_date.slice(0, 4)));
  /*
   * Both kinds, so the history tab shows the week they said they would be at a
   * client alongside the week they are asking off. The calendar below still
   * takes leave alone — see `useRequests`.
   */
  const theirs = useRequests({ employeeId: request.employee_id });

  const balance = balances.data?.balances.find((b) => b.leave_type_id === request.leave_type_id);
  const requestedDays = halvesToDays(request.total_halves);

  /** The dates this request covers, so the calendar can point at them. */
  const highlight = useMemo(
    () => new Set(datesOfRange(request.start_date, request.end_date)),
    [request.start_date, request.end_date],
  );

  const approve = useMutation({
    mutationFn: async () => {
      const result = await api.leaveRequests.approve(request.id, {
        approveExcessAsUnpaid: asUnpaid,
        note: note.trim() || undefined,
      });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (data) => {
      notify.success(
        `Approved — ${data?.days ?? 0} day(s) written to the grid` +
          (data?.split ? `. ${data.split}` : ""),
      );
      onDecided();
    },
    onError: (error: Error) => setShortfall(error.message),
  });

  const reject = useMutation({
    mutationFn: async () => {
      const result = await api.leaveRequests.reject(request.id, note.trim() || undefined);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Request rejected");
      onDecided();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const theirRequests = theirs.rows.filter((r) => !(r.kind === "leave" && r.id === request.id));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {request.employee_name ?? "Leave request"} — {requestedDays} day(s)
          </DialogTitle>
          <DialogDescription>
            {type?.name ?? "Leave"} · {spanLabel(request)}
            {halfNote(request) ? ` · ${halfNote(request)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* The arithmetic sits above the tabs rather than inside one of them:
            whichever tab is open, the number the decision turns on is "left if
            approved", and it should never be a tab away. */}
        <DecisionSummary
          balance={balance}
          requestedDays={requestedDays}
          loading={balances.isPending}
        />

        {/* Everything else is one thing at a time. Beside each other they were a
            wall — a year of calendar next to a column of figures, neither of
            them readable without the page having first been scrolled past the
            other. */}
        <Tabs defaultValue="ask" className="min-w-0 space-y-3">
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            <TabsTrigger value="ask" className="gap-2">
              <FileText className="h-3.5 w-3.5" />
              The ask
            </TabsTrigger>
            <TabsTrigger value="balances" className="gap-2">
              <Wallet className="h-3.5 w-3.5" />
              Balances
            </TabsTrigger>
            <TabsTrigger value="year" className="gap-2">
              <CalendarDays className="h-3.5 w-3.5" />
              Their year
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="h-3.5 w-3.5" />
              Their requests
              {theirRequests.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {theirRequests.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ask" className="max-w-2xl space-y-5">
            {request.reason ? (
              <blockquote className="border-muted-foreground/30 text-muted-foreground border-l-2 pl-4 text-sm leading-relaxed whitespace-pre-line">
                {request.reason}
              </blockquote>
            ) : (
              <p className="text-muted-foreground text-sm">They gave no reason.</p>
            )}

            <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              <Fact label="Leave type" value={type?.name ?? "—"} />
              <Fact label="Dates" value={spanLabel(request)} />
              <Fact
                label="Length"
                value={
                  halfNote(request)
                    ? `${requestedDays} day(s) · ${halfNote(request)}`
                    : `${requestedDays} day(s)`
                }
              />
              <Fact label="Asked for on" value={dayLabel(request.created_at.slice(0, 10))} />
            </dl>

            <div className="space-y-2">
              <Label htmlFor="decision-note">Note</Label>
              <Textarea
                id="decision-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Optional — kept on the request either way it goes."
              />
            </div>
          </TabsContent>

          <TabsContent value="balances" className="max-w-2xl space-y-5">
            {balance ? (
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  {balance.name} in {balance.year}
                </div>
                <div className="divide-y rounded-md border text-sm">
                  <BalanceLine label="Allowance" value={balance.total_days} />
                  <BalanceLine
                    label="Taken and booked"
                    value={balance.taken_days + balance.booked_days}
                  />
                  <BalanceLine
                    label="Remaining"
                    value={balance.remaining_days}
                    strong
                    negative={balance.remaining_days < 0}
                  />
                  <BalanceLine label="This request" value={-requestedDays} />
                  <BalanceLine
                    label="Left if approved"
                    value={leftIfApproved(balance, requestedDays)}
                    strong
                    negative={balance.remaining_days - requestedDays < 0}
                  />
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                {balances.isPending
                  ? "Reading the balance…"
                  : "This leave type carries no allowance."}
              </p>
            )}

            {/* Everything else they have left, because granting one type's days
                is a decision somebody makes knowing the rest of the year. */}
            <div className="space-y-2">
              <div className="text-sm font-medium">Their other balances</div>
              <OtherBalances
                employeeId={request.employee_id}
                year={Number(request.start_date.slice(0, 4))}
                exceptTypeId={request.leave_type_id}
              />
            </div>
          </TabsContent>

          <TabsContent value="year" className="min-w-0">
            <LeaveYearCalendar
              employeeId={request.employee_id}
              year={year}
              onYearChange={setYear}
              requests={theirs.leaveRows}
              typeById={typeById}
              highlight={highlight}
            />
          </TabsContent>

          <TabsContent value="history">
            <RequestsTable
              rows={theirRequests}
              typeById={typeById}
              loading={theirs.isPending}
              empty="They have asked for nothing else."
            />
          </TabsContent>
        </Tabs>

        {/* Outside the tabs: a refusal is about the decision, not about whatever
            tab happened to be open when it came back. */}
        {shortfall && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Not enough balance</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{shortfall}</p>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={asUnpaid}
                  onCheckedChange={(checked) => setAsUnpaid(checked === true)}
                  className="mt-0.5"
                />
                <span>
                  Approve the excess as <strong>unpaid leave</strong>. Those days will be deducted
                  at the half-day rate on the payslip.
                </span>
              </label>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => reject.mutate()} disabled={reject.isPending}>
            <X className="mr-2 h-4 w-4" />
            Reject
          </Button>
          <Button
            onClick={() => approve.mutate()}
            disabled={approve.isPending || (shortfall !== null && !asUnpaid)}
          >
            <Check className="mr-2 h-4 w-4" />
            {asUnpaid ? "Approve, excess unpaid" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Approving or rejecting a request to work away from the office.
 *
 * Deliberately a fraction of the leave dialog, because the decision genuinely
 * is. There is no balance to weigh: work from home consumes no allowance, is
 * never an absence and is paid as the ordinary day it is, so the figures the
 * leave dialog exists to put in front of somebody have no counterpart here.
 * What is left is the ask, the dates, and a note.
 *
 * The one thing it does insist on saying is what approval does *not* do. It
 * writes nothing onto the attendance grid — those days are still clocked, like
 * any other working day — and the approver is the person most likely to assume
 * otherwise, since the leave approval two rows up in the same queue does
 * exactly the opposite. See `docs/work-from-home-approval.md` §3.
 */
function WfhDecisionDialog({
  request,
  onClose,
  onDecided,
}: {
  request: RequestRow & { kind: "wfh" };
  onClose: () => void;
  onDecided: () => void;
}) {
  const [note, setNote] = useState("");

  const approve = useMutation({
    mutationFn: async () => {
      const result = await api.wfhRequests.approve(request.id, note.trim() || undefined);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success(
        `Approved — ${request.days} working day(s). The days still have to be clocked.`,
      );
      onDecided();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const reject = useMutation({
    mutationFn: async () => {
      const result = await api.wfhRequests.reject(request.id, note.trim() || undefined);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Request rejected");
      onDecided();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const deciding = approve.isPending || reject.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {request.employee_name ?? "Work from home"} — {request.days} working day(s)
          </DialogTitle>
          <DialogDescription>
            {REMOTE_STATUS_LABEL[request.proposes_status] ?? "Working from home"} ·{" "}
            {spanLabel(request)}
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Laptop className="h-4 w-4" />
          <AlertTitle>Approving this grants permission, not the days</AlertTitle>
          <AlertDescription>
            Nothing is written to the attendance grid. They still clock in and out on each of these
            days as they would in the office — what changes is that a clocked day reads as{" "}
            {REMOTE_STATUS_LABEL[request.proposes_status]?.toLowerCase() ?? "working from home"}{" "}
            instead of being queried. A day they never clock stays unanswered.
          </AlertDescription>
        </Alert>

        <div className="space-y-5">
          {request.reason ? (
            <blockquote className="border-muted-foreground/30 text-muted-foreground border-l-2 pl-4 text-sm leading-relaxed whitespace-pre-line">
              {request.reason}
            </blockquote>
          ) : (
            <p className="text-muted-foreground text-sm">They gave no reason.</p>
          )}

          <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
            <Fact
              label="Arrangement"
              value={REMOTE_STATUS_LABEL[request.proposes_status] ?? request.proposes_status}
            />
            <Fact label="Dates" value={spanLabel(request)} />
            {/* Weekends and public holidays are already out of this figure —
                the server counts the working days the dates actually cover, and
                re-counts them at the moment of approval. */}
            <Fact label="Working days" value={`${request.days} day(s)`} />
            <Fact label="Asked for on" value={dayLabel(request.source.created_at.slice(0, 10))} />
          </dl>

          <div className="space-y-2">
            <Label htmlFor="wfh-decision-note">Note</Label>
            <Textarea
              id="wfh-decision-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Optional — kept on the request either way it goes."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => reject.mutate()} disabled={deciding}>
            <X className="mr-2 h-4 w-4" />
            Reject
          </Button>
          <Button onClick={() => approve.mutate()} disabled={deciding}>
            <Check className="mr-2 h-4 w-4" />
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What the balance would be with this request granted, without float dust. */
function leftIfApproved(balance: LeaveBalanceRow, requestedDays: number): number {
  return Math.round((balance.remaining_days - requestedDays) * 100) / 100;
}

/**
 * The four figures a leave decision is, as a strip that stays put.
 *
 * It is the one thing the approver cannot be made to go looking for: whatever
 * they are reading — the reason, the calendar, last year's requests — the
 * question is still whether this request fits, and the rightmost cell answers
 * it.
 */
function DecisionSummary({
  balance,
  requestedDays,
  loading,
}: {
  balance: LeaveBalanceRow | undefined;
  requestedDays: number;
  loading: boolean;
}) {
  if (!balance) {
    return (
      <div className="bg-muted/30 text-muted-foreground rounded-lg border px-4 py-3 text-sm">
        {loading
          ? "Reading the balance…"
          : `This leave type carries no allowance — ${requestedDays} day(s) requested, with ` +
            "nothing to deduct them from."}
      </div>
    );
  }

  const left = leftIfApproved(balance, requestedDays);

  return (
    <div className="bg-border grid grid-cols-2 gap-px overflow-hidden rounded-lg border sm:grid-cols-4">
      <SummaryCell label="Allowance" value={balance.total_days} />
      <SummaryCell label="Taken and booked" value={balance.taken_days + balance.booked_days} />
      <SummaryCell
        label="Remaining"
        value={balance.remaining_days}
        negative={balance.remaining_days < 0}
      />
      <SummaryCell label="Left if approved" value={left} negative={left < 0} emphasis />
    </div>
  );
}

function SummaryCell({
  label,
  value,
  negative,
  emphasis,
}: {
  label: string;
  value: number;
  negative?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div className={cn("bg-background px-4 py-3", emphasis && "bg-muted/40")}>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div
        className={cn(
          "text-lg font-semibold tabular-nums",
          negative && "text-red-600 dark:text-red-400",
          emphasis && !negative && "text-emerald-700 dark:text-emerald-400",
        )}
      >
        {value}
      </div>
    </div>
  );
}

/** One line of the balance breakdown. */
function BalanceLine({
  label,
  value,
  strong,
  negative,
}: {
  label: string;
  value: number;
  strong?: boolean;
  negative?: boolean;
}) {
  return (
    <div className={cn("flex justify-between px-3 py-2", strong && "font-semibold")}>
      <span>{label}</span>
      <span className={cn("tabular-nums", negative && "text-red-600 dark:text-red-400")}>
        {value < 0 ? `−${Math.abs(value)}` : value}
      </span>
    </div>
  );
}

/** A labelled fact, for the short list of what was actually asked for. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/** The remaining days of every other type, compact enough to sit beside a decision. */
function OtherBalances({
  employeeId,
  year,
  exceptTypeId,
}: {
  employeeId: string;
  year: number;
  exceptTypeId: string;
}) {
  const balances = useBalances(employeeId, year);

  const rows = (balances.data?.balances ?? []).filter(
    (b) => b.consumes_allowance && b.leave_type_id !== exceptTypeId,
  );

  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {balances.isPending ? "Reading…" : "No other type carries an allowance."}
      </p>
    );
  }

  return (
    <div className="divide-y rounded-md border text-sm">
      {rows.map((balance) => (
        <div key={balance.leave_type_id} className="flex justify-between gap-4 px-3 py-1.5">
          <span>{balance.name}</span>
          {/* "left of" rather than "of": `0 of 17` was read as nought taken out
              of seventeen, which is the exact opposite of what it says. The
              figure is what remains, and the word has to carry that. */}
          <span
            className={cn(
              "tabular-nums whitespace-nowrap",
              balance.remaining_days < 0
                ? "text-red-600 dark:text-red-400"
                : balance.remaining_days === 0
                  ? "text-foreground font-medium"
                  : "text-muted-foreground",
            )}
          >
            {balance.remaining_days} left of {balance.total_days}
          </span>
        </div>
      ))}
    </div>
  );
}
